import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { config } from '@/config';

interface PostHogProviderProps {
  children: React.ReactNode;
}

/**
 * PostHogProvider - Initializes PostHog analytics SDK (WP-LANDING-2)
 *
 * Uses the official PostHog React integration which:
 * - Properly handles async feature flag loading
 * - Provides useFeatureFlagVariantKey hook for A/B tests
 * - Re-renders components when feature flags load
 *
 * Configuration:
 * - POSTHOG_KEY: API key (required for analytics to activate)
 * - POSTHOG_HOST: Custom host URL (defaults to https://app.posthog.com)
 */
export function PostHogProvider({ children }: PostHogProviderProps) {
  const [isInitialized, setIsInitialized] = useState(() => posthog.__loaded ?? false);

  // Initialize PostHog on mount (once)
  useEffect(() => {
    const apiKey = config.posthogKey;
    const apiHost = config.posthogHost;

    // Skip initialization if no API key configured
    if (!apiKey) {
      return;
    }

    // Check if already initialized
    if (posthog.__loaded) {
      setIsInitialized(true);
      return;
    }

    posthog.init(apiKey, {
      api_host: apiHost,
      // Capture pageviews automatically
      capture_pageview: false, // We'll handle this manually on route changes
      // Privacy settings - disable DNT to ensure analytics work
      respect_dnt: false,
      // Disable session recording for now (can enable later)
      disable_session_recording: true,
      // Persistence
      persistence: 'localStorage+cookie',
      // Only send data when page is visible
      capture_pageleave: true,
      // Opt out of autocapture to be more targeted
      autocapture: false,
      // Enable debug mode in development
      loaded: (ph) => {
        if (config.environment === 'development') {
          ph.debug();
        }
      },
    });

    // Mark as initialized immediately after init() call
    // The PHProvider and hooks handle async flag loading internally
    setIsInitialized(true);
  }, []);

  // If PostHog not configured, just render children
  if (!config.posthogKey) {
    return <>{children}</>;
  }

  // Wait for PostHog to be initialized before wrapping with provider
  // Note: We wrap immediately after init(), hooks handle async flag loading
  if (!isInitialized) {
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      <PageviewTracker />
      {children}
    </PHProvider>
  );
}

/**
 * Tracks pageviews on route changes
 */
function PageviewTracker() {
  const location = useLocation();

  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: location.pathname,
    });
  }, [location.pathname]);

  return null;
}

export default PostHogProvider;
